<template>
  <div>
    <b-card no-body class="m-0 mb-2">
      <div class="d-flex justify-content-between align-items-center px-1 pt-1">
        <h5 class="mb-0">Vendor category requests</h5>
        <b-button size="sm" variant="outline-secondary" :disabled="requestsLoading" @click="fetchCategoryRequests">
          Refresh
        </b-button>
      </div>
      <div class="m-2">
        <div v-if="requestsLoading" class="text-center text-secondary my-2">
          <b-spinner class="align-middle" />
        </div>
        <b-table
          v-else
          :items="pendingRequests"
          responsive
          :fields="requestColumns"
          show-empty
          empty-text="No pending vendor category requests"
        >
          <template #cell(company_name)="data">
            {{ data.item.company_name || '—' }}
          </template>
          <template #cell(name)="data">
            {{ data.item.name }}
          </template>
          <template #cell(requester_email)="data">
            {{ data.item.requester_email || data.item.requester_name || '—' }}
          </template>
          <template #cell(actions)="data">
            <div class="d-flex align-items-center">
              <b-button size="sm" variant="success" class="mr-50" :disabled="requestBusyId === data.item.id" @click="approveRequest(data.item)">
                Approve
              </b-button>
              <b-button size="sm" variant="outline-danger" :disabled="requestBusyId === data.item.id" @click="rejectRequest(data.item)">
                Reject
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-card no-body class="m-0">
      <b-row class="d-flex justify-content-between gap-1">
        <b-col class="mt-1 d-flex pagination-button">
          <v-select
            v-model="perPage"
            :clearable="false"
            class="per-page-selector ml-1"
            style="width: 100px"
            :options="['5', '8', '10', '20', '50']"
            @input="reloadPageOne"
          />
          <b-button variant="primary" class="category-primary-btn" @click="openCreateModal">
            Add Category
          </b-button>
        </b-col>
        <div class="ml-1 mt-1 mr-1">
          <b-form-input
            v-model="search"
            placeholder="Search categories..."
            class="mr-1"
            style="width: 220px"
            @input="debouncedSearch"
          />
        </div>
      </b-row>

      <div class="m-2">
        <b-table
          :busy="isLoading"
          :items="rows"
          responsive
          :fields="tableColumns"
          show-empty
          empty-text="No categories found"
        >
          <template #table-busy>
            <div class="text-center text-secondary my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>
          <template #cell(no)="data">
            <span>{{ String(data.item.no).padStart(2, '0') }}</span>
          </template>
          <template #cell(image)="data">
            <b-avatar
              v-if="data.item.imageUrl"
              :src="data.item.imageUrl"
              size="40"
              variant="light"
            />
            <b-avatar
              v-else
              size="40"
              variant="light-secondary"
              :text="data.item.imageFallback"
            />
          </template>
          <template #cell(name)="data">
            <span :class="{ 'pl-2 text-muted': data.item.parentId }">
              <span v-if="data.item.parentId" class="mr-50">↳</span>
              {{ data.item.name }}
            </span>
          </template>
          <template #cell(parentName)="data">
            <span>{{ data.item.parentName || '—' }}</span>
          </template>
          <template #cell(type)="data">
            <b-badge :variant="data.item.parentId ? 'light-info' : 'light-primary'">
              {{ data.item.parentId ? 'Child' : 'Parent' }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <row-actions
              @edit="openEditModal(data.item)"
              @delete="confirmDelete(data.item)"
            />
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center px-1 pb-1">
          <small class="text-muted">{{ paginationLabel }}</small>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPageNum"
            size="sm"
            pills
            prev-text="Previous"
            next-text="Next"
          />
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="categoryModal"
      hide-footer
      no-close-on-backdrop
      centered
      size="md"
      :title="isEdit ? 'Edit Category' : 'Add Category'"
      @hidden="resetModal"
    >
      <validation-observer ref="categoryForm">
        <b-row>
          <b-col cols="12">
            <mypoda-form-field
              label="Name"
              name="Name"
              rules="required|max:255"
              required-mark
            >
              <template #default="{ state, inputId }">
                <b-form-input
                  :id="inputId"
                  v-model="form.name"
                  :state="state"
                  placeholder="Category name"
                />
              </template>
            </mypoda-form-field>
          </b-col>
          <b-col cols="12">
            <label class="mypoda-field-label d-block mb-50">Parent category</label>
            <b-form-select
              v-model="form.parentId"
              :options="parentOptions"
              :disabled="parentSelectDisabled"
            >
              <template #first>
                <b-form-select-option :value="null">
                  None (top-level parent)
                </b-form-select-option>
              </template>
            </b-form-select>
            <small v-if="parentSelectDisabled" class="text-muted d-block mt-25">
              This category has children, so it must stay a top-level parent.
            </small>
            <small v-else-if="!parentOptions.length" class="text-muted d-block mt-25">
              Create a top-level parent first, then you can nest child categories under it.
            </small>
            <small v-else class="text-muted d-block mt-25">
              Leave empty for a parent (primary) category, or pick a parent to create a child (secondary).
            </small>
          </b-col>
          <b-col cols="12">
            <label class="mypoda-field-label d-block mb-50">Image</label>
            <div class="d-flex align-items-center flex-wrap">
              <b-avatar
                v-if="form.imagePreview"
                :src="form.imagePreview"
                size="64"
                class="mr-1 mb-50"
              />
              <b-button
                type="button"
                variant="outline-primary"
                size="sm"
                class="mb-50 mr-50"
                @click.prevent="openImagePicker"
              >
                {{ form.imagePreview ? 'Change image' : 'Upload image' }}
              </b-button>
              <b-button
                v-if="form.imagePreview"
                variant="outline-secondary"
                size="sm"
                class="mb-50"
                @click="clearImage"
              >
                Remove
              </b-button>
            </div>
            <input
              ref="imageInput"
              type="file"
              accept="image/*"
              class="d-none"
              @change="onImageSelected"
            />
          </b-col>
          <b-col cols="12" class="d-flex justify-content-start mt-2">
            <b-button variant="primary" class="category-primary-btn mr-1" :disabled="saving" @click.prevent="submitForm">
              <b-spinner v-if="saving" small class="mr-50" />
              {{ isEdit ? 'Update' : 'Create' }}
            </b-button>
            <b-button variant="outline-secondary" @click="closeModal">
              Cancel
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  BModal,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { adminPanelApi } from '@/services/adminPanelApi'
import { debounce, resolveMediaUrl, unwrapAdminResponse } from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess, promptRejectReason } from '@/utils/uiActions'
import RowActions from '@/components/admin/RowActions.vue'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'

const emptyForm = () => ({
  name: '',
  parentId: null,
  imageFile: null,
  imagePreview: '',
  existingImage: '',
})

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    BModal,
    ValidationObserver,
    vSelect,
    RowActions,
    MypodaFormField,
  },
  data() {
    return {
      isLoading: false,
      saving: false,
      categoryModal: false,
      isEdit: false,
      selectedId: null,
      search: '',
      currentPage: 1,
      perPage: '8',
      allCategories: [],
      pendingRequests: [],
      requestsLoading: false,
      requestBusyId: null,
      form: emptyForm(),
      requestColumns: [
        { key: 'company_name', label: 'VENDOR' },
        { key: 'name', label: 'REQUESTED CATEGORY' },
        { key: 'requester_email', label: 'REQUESTED BY' },
        { key: 'actions', label: 'ACTION' },
      ],
      tableColumns: [
        { key: 'no', label: 'SR NO.' },
        { key: 'image', label: 'IMAGE' },
        { key: 'name', label: 'NAME' },
        { key: 'type', label: 'TYPE' },
        { key: 'parentName', label: 'PARENT' },
        { key: 'actions', label: 'ACTION' },
      ],
    }
  },
  computed: {
    perPageNum() {
      return parseInt(this.perPage, 10) || 8
    },
    filteredCategories() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.allCategories
      return this.allCategories.filter(item => {
        const name = String(item.name || '').toLowerCase()
        const parent = String(item.parentName || '').toLowerCase()
        return name.includes(term) || parent.includes(term)
      })
    },
    totalRows() {
      return this.filteredCategories.length
    },
    rows() {
      const start = (this.currentPage - 1) * this.perPageNum
      return this.filteredCategories.slice(start, start + this.perPageNum).map((item, index) => ({
        ...item,
        no: start + index + 1,
      }))
    },
    paginationLabel() {
      if (!this.totalRows) return 'No entries'
      const start = (this.currentPage - 1) * this.perPageNum + 1
      const end = Math.min(this.currentPage * this.perPageNum, this.totalRows)
      return `Showing ${start} to ${end} of ${this.totalRows} entries`
    },
    parentOptions() {
      const selectedId = Number(this.selectedId || 0)
      return this.allCategories
        .filter(item => !item.parentId && Number(item.id) !== selectedId)
        .map(item => ({
          value: item.id,
          text: item.name,
        }))
    },
    parentSelectDisabled() {
      if (!this.isEdit || !this.selectedId) return false
      const selectedId = Number(this.selectedId)
      return this.allCategories.some(item => Number(item.parentId) === selectedId)
    },
  },
  watch: {
    totalRows(total) {
      const lastPage = Math.max(1, Math.ceil(total / this.perPageNum))
      if (this.currentPage > lastPage) {
        this.currentPage = lastPage
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.currentPage = 1
    }, 400)
    this.fetchCategories()
    this.fetchCategoryRequests()
  },
  methods: {
    reloadPageOne() {
      this.currentPage = 1
    },
    mapCategoryRow(item) {
      const rawImage = item.image || item.icon || ''
      const imageUrl = this.isImageValue(rawImage) ? resolveMediaUrl(rawImage) : ''
      const parentId = item.parent_id != null ? Number(item.parent_id) : null
      return {
        id: item.id,
        name: item.name || '',
        parentId: Number.isFinite(parentId) && parentId > 0 ? parentId : null,
        parentName: item.parent_name || '',
        imageUrl,
        imageFallback: (item.name || '?').slice(0, 2).toUpperCase(),
        rawImage,
      }
    },
    isImageValue(value) {
      if (!value) return false
      const text = String(value).trim()
      return /^https?:\/\//i.test(text) || text.includes('/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(text)
    },
    async fetchCategories() {
      this.isLoading = true
      try {
        const res = await adminPanelApi.categories()
        const payload = unwrapAdminResponse(res)
        const list = payload?.categories || payload || []
        this.allCategories = (Array.isArray(list) ? list : []).map(item => this.mapCategoryRow(item))
      } catch (e) {
        this.allCategories = []
        notifyError(this, e.response?.data?.message || 'Unable to load categories.')
      } finally {
        this.isLoading = false
      }
    },
    async fetchCategoryRequests() {
      this.requestsLoading = true
      try {
        const res = await adminPanelApi.categoryRequests({ status: 'pending' })
        const payload = unwrapAdminResponse(res)
        this.pendingRequests = Array.isArray(payload?.requests) ? payload.requests : []
      } catch (e) {
        this.pendingRequests = []
      } finally {
        this.requestsLoading = false
      }
    },
    approveRequest(item) {
      confirmAction(
        this,
        {
          title: 'Approve category?',
          text: `Create "${item.name}" and assign it to ${item.company_name || 'this vendor'}?`,
          confirmButtonText: 'Approve',
        },
        async () => {
          this.requestBusyId = item.id
          try {
            await adminPanelApi.approveCategoryRequest(item.id)
            notifySuccess(this, 'Category request approved.')
            await Promise.all([this.fetchCategoryRequests(), this.fetchCategories()])
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to approve request.')
          } finally {
            this.requestBusyId = null
          }
        },
      )
    },
    async rejectRequest(item) {
      const reason = await promptRejectReason(this, {
        title: 'Reject category request',
        text: `Provide a reason for rejecting "${item.name}".`,
      })
      if (!reason) return
      this.requestBusyId = item.id
      try {
        await adminPanelApi.rejectCategoryRequest(item.id, { reason })
        notifySuccess(this, 'Category request rejected.')
        await this.fetchCategoryRequests()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to reject request.')
      } finally {
        this.requestBusyId = null
      }
    },
    openCreateModal() {
      this.isEdit = false
      this.selectedId = null
      this.form = emptyForm()
      this.categoryModal = true
    },
    openEditModal(item) {
      this.isEdit = true
      this.selectedId = item.id
      this.form = {
        name: item.name || '',
        parentId: item.parentId || null,
        imageFile: null,
        imagePreview: item.imageUrl || '',
        existingImage: item.rawImage || '',
      }
      this.categoryModal = true
    },
    closeModal() {
      this.categoryModal = false
    },
    resetModal() {
      this.isEdit = false
      this.selectedId = null
      this.form = emptyForm()
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = ''
      }
      if (this.$refs.categoryForm) {
        this.$refs.categoryForm.reset()
      }
    },
    openImagePicker() {
      this.$refs.imageInput?.click()
    },
    onImageSelected(event) {
      const file = event?.target?.files?.[0]
      if (!file) return
      this.form.imageFile = file
      this.form.imagePreview = URL.createObjectURL(file)
    },
    clearImage() {
      this.form.imageFile = null
      this.form.imagePreview = ''
      this.form.existingImage = ''
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = ''
      }
    },
    async uploadCategoryImage(file) {
      const formData = new FormData()
      formData.append('file', file)
      const res = await adminPanelApi.uploadImage(formData)
      const data = unwrapAdminResponse(res)
      if (typeof data === 'string') return data
      return data?.url || data?.path || ''
    },
    async submitForm() {
      const valid = await this.$refs.categoryForm.validate()
      if (!valid) return

      this.saving = true
      try {
        let imageValue = this.form.existingImage || null
        if (this.form.imageFile) {
          imageValue = await this.uploadCategoryImage(this.form.imageFile)
          if (!imageValue) {
            notifyError(this, 'Unable to upload category image.')
            return
          }
        }

        const payload = {
          name: this.form.name.trim(),
          icon: imageValue,
          sort_order: 0,
          is_active: true,
          parent_id: this.parentSelectDisabled ? null : (this.form.parentId || null),
        }

        if (this.isEdit && this.selectedId) {
          await adminPanelApi.updateCategory(this.selectedId, payload)
          notifySuccess(this, 'Category updated successfully.')
        } else {
          await adminPanelApi.createCategory(payload)
          notifySuccess(this, 'Category created successfully.')
          this.currentPage = 1
        }
        this.closeModal()
        await this.fetchCategories()
      } catch (e) {
        const apiErrors = e.response?.data?.errors
        const firstFieldError = apiErrors && Object.values(apiErrors).flat()[0]
        notifyError(this, firstFieldError || e.response?.data?.message || 'Unable to save category.')
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      const hasChildren = this.allCategories.some(row => row.parentId === item.id)
      if (hasChildren) {
        notifyError(
          this,
          `"${item.name}" has child categories. Delete or reassign those first.`,
        )
        return
      }
      confirmAction(
        this,
        {
          title: 'Delete category?',
          text: `Remove "${item.name}"?`,
          confirmButtonText: 'Delete',
        },
        async () => {
          try {
            await adminPanelApi.deleteCategory(item.id)
            notifySuccess(this, 'Category deleted successfully.')
            await this.fetchCategories()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete category.')
          }
        },
      )
    },
  },
}
</script>

<style scoped>
.pagination-button {
  display: flex;
  gap: 10px;
}
.gap-1 {
  gap: 10px;
}
.cursor-pointer {
  cursor: pointer;
}

.category-primary-btn {
  background-color: #011425 !important;
  border-color: #011425 !important;
}

.category-primary-btn:hover,
.category-primary-btn:focus,
.category-primary-btn:active,
.category-primary-btn:not(:disabled):not(.disabled):active {
  background-color: #011425 !important;
  border-color: #011425 !important;
  opacity: 0.92;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

